import { ReactElement, ReactNode, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectProps,
	Skeleton,
} from "@mui/material";
import { Controller, FieldValues, Path, PathValue, RegisterOptions, UseFormReturn } from "react-hook-form";
import { InfoIcon } from "../../assets/icons";
import { Option } from "../../datasources/responses/common";
import FormFieldHint from "./FormFieldHint";

export type DialogProps = {
	title: string;
	content: ReactElement;
};

interface Props<T extends FieldValues> extends Omit<SelectProps, "name" | "label" | "control" | "options"> {
	name: Path<T>;
	label: string;
	readOnly?: boolean;
	isLoading?: boolean;
	isOptional?: boolean;
	hintMessage?: string;
	form: UseFormReturn<T>;
	noSelectionOption?: string;
	options: Array<Option>;
	afterChange?: (v: any) => void;
	rules?: Omit<RegisterOptions<T, Path<T>>, "disabled" | "valueAsNumber" | "valueAsDate" | "setValueAs">;
	dialog?: DialogProps;
}

/**
 * FormSelect acts as a wrapper component for selects used in conjunction with React Hook Form.
 *
 * @param name              the name of the field bound to this select
 * @param label             the label of the select
 * @param readOnly          if true, makes the field read-only
 * @param isLoading         if true, the component will render a skeleton element
 * @param isOptional        if true, the select will show an empty "no option selected" value
 * @param form              the form reference
 * @param afterChange       if defined, this callback is invoked right after internal onChange event
 * @param noSelectionOption if defined and not the empty string "", the select will
 *                          render a "no selection" option with the given text
 * @param options           the list of options of this select
 * @param hintMessage       the optional hint message that may be rendered at the end of the field
 * @param rules             optional validation rules (if undefined, the field will be optional)
 * @param dialog	   		if true, an info icon is rendered at the end of the field. Clicking on it opens a dialog showing the content of the "info" prop
 * 		              		the content to be shown in the dialog triggered by the "dialog" prop
 * @returns                 a Select wrapped in a React Hook Form Control component
 */
const FormSelect = <T extends FieldValues>({
	name,
	form,
	label,
	rules,
	options,
	readOnly,
	isLoading,
	hintMessage,
	afterChange,
	noSelectionOption,
	dialog,
	...rest
}: Props<T>): React.ReactElement => {
	const [openDialog, setOpenDialog] = useState(false);

	// Show "*" right after mandatory fields (i.e. those to which validation rules are applied).
	const isRequired = !!rules?.required;
	const finalLabel = isRequired ? `${label} *` : label;

	// Variable "values" is used internally and allows support for "no selection" option.
	const allowNoSelection = noSelectionOption !== undefined && noSelectionOption !== "";
	const values =
		allowNoSelection || options.length <= 0 ? [{ id: "", label: noSelectionOption }, ...options] : options;

	const defaultValue = (allowNoSelection ? "" : (values[0]?.id ?? "")) as PathValue<T, Path<T>>;

	const valueDidChange = (v: unknown, onChange: (...event: any[]) => void) => {
		onChange(v);
		afterChange?.(v);

		if (form.formState.isSubmitted) {
			form.trigger(name);
		}
	};

	const renderValue = (v: unknown): ReactNode => {
		if (v === "" && noSelectionOption) return <i>{noSelectionOption}</i>;
		return values.find((o) => o.id === v)?.label ?? "";
	};

	if (isLoading) {
		return <Skeleton height={"44px"} variant="rounded" className="loading-field" />;
	}

	return (
		<>
			<Controller
				name={name}
				control={form.control}
				defaultValue={defaultValue}
				rules={rules}
				render={({ field: { onChange, value }, fieldState: { error } }) => (
					<FormControl fullWidth variant="outlined" error={!!error} size="small">
						<InputLabel id={`${name}-selector`}>{finalLabel}</InputLabel>
						<Select
							{...rest}
							value={value ?? ""}
							className={readOnly ? "read-only" : undefined}
							displayEmpty={allowNoSelection}
							labelId={`${name}-selector`}
							label={finalLabel}
							variant="outlined"
							size="small"
							fullWidth
							inputProps={{ readOnly }}
							renderValue={renderValue}
							onChange={(e) => valueDidChange(e.target.value, onChange)}
							startAdornment={
								dialog && (
									<IconButton onClick={() => setOpenDialog(true)} size="small">
										<InfoIcon color="info" />
									</IconButton>
								)
							}
						>
							{values.map((v, k) => (
								<MenuItem key={`${v.id}-${k}`} value={v.id}>
									{v.id === "" ? <i>{v.label}</i> : v.label}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>{error?.message ?? <FormFieldHint text={hintMessage} />}</FormHelperText>
					</FormControl>
				)}
			/>
			<Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
				<DialogTitle>{dialog?.title}</DialogTitle>
				<DialogContent>{dialog?.content}</DialogContent>
			</Dialog>
		</>
	);
};

export default FormSelect;
